import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Stepper, 
  Step, 
  StepLabel, 
  Typography, 
  Paper,
  Button,
  CircularProgress,
  Divider 
} from '@mui/material';
import axios from '../config/axios';
import { useTheme } from '../context/ThemeContext';
import IdentificationDialog from './IdentificationDialog';
import ClientImageDialog from './ClientImageDialog';
import ProcessClientDialog from './ProcessClientDialog';
import IntegrityDialog from './IntegrityDialog';

// Mantenemos todos los pasos pero solo trabajamos con los primeros por ahora
const steps = [
  { label: 'Identificación - Imagen', description: 'Generación de imagen de identificación' },
  { label: 'Identificación - Envío', description: 'Envío de identificación' },
  { label: 'Identificación - Confirmada', description: 'Confirmación de identificación' },
  { label: 'Integridad - Email', description: 'Envío de correo electrónico' },
  { label: 'Integridad - Recibido', description: 'Confirmación de recepción' },
  { label: 'Integridad - Leído', description: 'Confirmación de lectura' },
  { label: 'Integridad - Aceptado', description: 'Aceptación del contrato' }
];

function FlowDetails({ contract }) {
  const { themeColors } = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [flowStatus, setFlowStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showIdentification, setShowIdentification] = useState(false);
  const [showProcessClient, setShowProcessClient] = useState(false);
  const [showClientImage, setShowClientImage] = useState(false);
  const [showIntegrityDialog, setShowIntegrityDialog] = useState(false);

  const loadFlowStatus = async () => {
    if (!contract?.id) {
      console.warn('No hay ID de contrato');
      return;
    }

    try {
      setLoading(true);
      console.log('Cargando flow status para contrato:', contract.id);
      
      const response = await axios.get(`/api/contracts/${contract.id}/flow-status`);
      console.log('Flow status response:', response.data);
      
      if (response.data) {
        setFlowStatus(response.data);
        updateActiveStep(response.data);
        console.log('Paso activo actualizado a:', activeStep);
      } else {
        console.warn('No se recibieron datos del flow status');
      }
    } catch (err) {
      console.error('Error al cargar flow status:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        config: err.config
      });
    } finally {
      setLoading(false);
    }
  };

  // Actualizar el paso activo basado en el estado
  const updateActiveStep = (status) => {
    if (!status) return;
    
    console.log('Estado actual identificación:', status.identificacion.estado);
    console.log('Estado actual integridad:', status.integridad.estado);
    
    switch (status.identificacion.estado) {
      case 'COMPLETADO':
        setActiveStep(3);
        console.log('Paso actualizado a: 3 (COMPLETADO)');
        break;
      case 'RECIBIDO':
        setActiveStep(2);
        console.log('Paso actualizado a: 2 (RECIBIDO)');
        break;
      case 'ENVIADO':
        setActiveStep(1);
        console.log('Paso actualizado a: 1 (ENVIADO)');
        break;
      default:
        setActiveStep(0);
        console.log('Paso actualizado a: 0 (DEFAULT)');
    }

    // Luego verificamos integridad si es necesario
    if (status.identificacion.estado === 'COMPLETADO') {
      switch (status.integridad.estado) {
        case 'COMPLETADO':
          setActiveStep(6); // Contrato aceptado
          break;
        case 'LEIDO':
          setActiveStep(5); // Integridad leída
          break;
        case 'RECIBIDO':
          setActiveStep(4); // Integridad recibida
          break;
        case 'ENVIADO':
          setActiveStep(3); // Email enviado
          break;
        case 'NO_INICIADO':
        default:
          setActiveStep(3); // Listo para iniciar integridad
      }
    }
  };

  // Mostrar solo el botón correspondiente al paso actual
  const renderActionButton = () => {
    if (!flowStatus) return null;

    switch (flowStatus.identificacion.estado) {
      case 'NO_INICIADO':
        return (
          <Button
            variant="contained"
            onClick={() => setShowIdentification(true)}
            disabled={flowStatus?.identificacion?.aceptado}
          >
            Generar Identificación
          </Button>
        );
      
      case 'ENVIADO':
      case 'LEIDO':
        return (
          <Button
            variant="contained"
            onClick={() => setShowProcessClient(true)}
            disabled={flowStatus?.identificacion?.recibido}
          >
            Procesar Cliente
          </Button>
        );
      
      case 'RECIBIDO':
        return (
          <Button
            variant="contained"
            onClick={() => setShowClientImage(true)}
            disabled={false}
          >
            Cliente con Imagen
          </Button>
        );
      
      case 'COMPLETADO':
        return (
          <Button
            variant="contained"
            onClick={() => setShowIntegrityDialog(true)}
            disabled={flowStatus?.integridad?.aceptado}
          >
            Proceso de Integridad
          </Button>
        );
      
      default:
        return null;
    }
  };

  useEffect(() => {
    if (contract?.id) {
      console.log('ID de contrato cambió:', contract.id);
      loadFlowStatus();
    }
  }, [contract?.id]);

  if (!contract?.id) return null;

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Proceso de Contrato Digital
      </Typography>

      <Stepper activeStep={activeStep} sx={{ my: 3 }}>
        {steps.map((step) => (
          <Step key={step.label}>
            <StepLabel>
              <Typography variant="body2">{step.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {step.description}
              </Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Divider sx={{ my: 2 }} />

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          {renderActionButton()}
        </Box>
      )}

      {/* Diálogos */}
      <IdentificationDialog
        open={showIdentification}
        contractId={contract?.id}
        contract={contract}
        onClose={(success) => {
          setShowIdentification(false);
          if (success) loadFlowStatus();
        }}
      />

      <ProcessClientDialog
        open={showProcessClient}
        contractId={contract.id}
        contract={contract}
        onClose={(success) => {
          setShowProcessClient(false);
          if (success) loadFlowStatus();
        }}
      />

      <ClientImageDialog
        open={showClientImage}
        contractId={contract.id}
        onClose={(success) => {
          setShowClientImage(false);
          if (success) loadFlowStatus();
        }}
      />

      <IntegrityDialog
        open={showIntegrityDialog}
        contractId={contract?.id}
        onClose={(success) => {
          setShowIntegrityDialog(false);
          if (success) loadFlowStatus();
        }}
      />
    </Paper>
  );
}

export default FlowDetails; 